/* I periodi del controllo: le mascherine veloci, e le date che ne escono.
 *
 * Nato il 17 ottobre 2026 per la scheda Periodo, e usato anche da Gestione, cosi' un trimestre
 * e' lo stesso trimestre in tutte e due. **Solo date scritte come testo, mai `Date`**: un fuso
 * orario che sposta di un giorno il 30 giugno e' il tipo di errore che nessuno vede finche' un
 * incasso del primo luglio finisce nel semestre sbagliato.
 *
 * L'anno a cavallo va dal 1 luglio al 30 giugno dell'anno dopo, ed e' contato dall'anno in cui
 * comincia: "2026" vuol dire da luglio 2026 a giugno 2027.
 */

export type FormaPeriodo = "anno" | "s1" | "s2" | "t1" | "t2" | "t3" | "t4" | "cavallo" | "libero";

export interface Intervallo {
  dal: string;
  al: string;
}

/** Cio' che la persona ha scelto: la mascherina e l'anno, oppure due date a mano. */
export interface Selezione extends Intervallo {
  forma: FormaPeriodo;
  anno: number;
}

export const MASCHERINE: { forma: Exclude<FormaPeriodo, "libero">; nome: string }[] = [
  { forma: "anno", nome: "Anno" },
  { forma: "s1", nome: "1° sem." },
  { forma: "s2", nome: "2° sem." },
  { forma: "t1", nome: "1° trim." },
  { forma: "t2", nome: "2° trim." },
  { forma: "t3", nome: "3° trim." },
  { forma: "t4", nome: "4° trim." },
  { forma: "cavallo", nome: "Anno a cavallo" },
];

export function intervalloDi(forma: Exclude<FormaPeriodo, "libero">, anno: number): Intervallo {
  switch (forma) {
    case "anno":
      return { dal: `${anno}-01-01`, al: `${anno}-12-31` };
    case "s1":
      return { dal: `${anno}-01-01`, al: `${anno}-06-30` };
    case "s2":
      return { dal: `${anno}-07-01`, al: `${anno}-12-31` };
    case "t1":
      return { dal: `${anno}-01-01`, al: `${anno}-03-31` };
    case "t2":
      return { dal: `${anno}-04-01`, al: `${anno}-06-30` };
    case "t3":
      return { dal: `${anno}-07-01`, al: `${anno}-09-30` };
    case "t4":
      return { dal: `${anno}-10-01`, al: `${anno}-12-31` };
    case "cavallo":
      return { dal: `${anno}-07-01`, al: `${anno + 1}-06-30` };
  }
}

export function selezione(forma: Exclude<FormaPeriodo, "libero">, anno: number): Selezione {
  return { forma, anno, ...intervalloDi(forma, anno) };
}

/** Il nome che si legge in pagina: "3° trimestre 2026", "Anno a cavallo 2026 / 2027". */
export function nomePeriodo(s: Selezione): string {
  switch (s.forma) {
    case "anno":
      return `Anno ${s.anno}`;
    case "s1":
      return `1° semestre ${s.anno}`;
    case "s2":
      return `2° semestre ${s.anno}`;
    case "t1":
    case "t2":
    case "t3":
    case "t4":
      return `${s.forma.slice(1)}° trimestre ${s.anno}`;
    case "cavallo":
      return `Anno a cavallo, da luglio ${s.anno} a giugno ${s.anno + 1}`;
    case "libero":
      return "Periodo scelto";
  }
}

function dataLeggibile(iso: string): string {
  const [anno, mese, giorno] = iso.split("-");
  return `${giorno}/${mese}/${anno}`;
}

/** Le date esatte, sempre sotto le mascherine: chi legge sa cosa sta guardando senza indovinare. */
export function estremi(i: Intervallo): string {
  return `dal ${dataLeggibile(i.dal)} al ${dataLeggibile(i.al)}`;
}

/** Due date a mano sono valide se sono date vere e la prima non viene dopo la seconda.
 *
 * "Vera" vuol dire che esiste sul calendario: il 30 febbraio ha la forma giusta e `Date.parse` lo
 * accetta, rotolandolo a marzo, per questo si ricostruisce la data e si confronta col testo. */
export function intervalloValido(i: Intervallo): boolean {
  const vera = (iso: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
    const [a, m, g] = iso.split("-").map(Number) as [number, number, number];
    const d = new Date(Date.UTC(a, m - 1, g));
    return d.getUTCFullYear() === a && d.getUTCMonth() === m - 1 && d.getUTCDate() === g;
  };
  return vera(i.dal) && vera(i.al) && i.dal <= i.al;
}
